import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import {
  NotificationDto,
  NotificationListResponse,
} from '../../interfaces/notification';
import {
  ApiSimpleResponse,
  ApiSingleResponse,
} from '../../interfaces/responses/api-response';

@Injectable({ providedIn: 'root' })
export class NotificationService {
  private readonly base = `${environment.BASE_URL}/notifications`;

  private _unreadCount = new BehaviorSubject<number>(0);
  /** Observable unread badge count. */
  readonly unreadCount$ = this._unreadCount.asObservable();

  private _notifications = new BehaviorSubject<NotificationDto[]>([]);
  readonly notifications$ = this._notifications.asObservable();

  constructor(private http: HttpClient) {}

  /**
   * Charge les notifications de l'utilisateur.
   *
   * La réponse est une enveloppe à objet unique : `data` porte
   * `{ items, unreadCount }`. Le compteur de non-lus vient du serveur — le
   * recalculer depuis la page courante donnerait un badge faux dès que le
   * nombre de notifications dépasse `take`.
   */
  list(
    skip = 0,
    take = 50,
  ): Observable<ApiSingleResponse<NotificationListResponse>> {
    const params = new HttpParams()
      .set('skip', skip.toString())
      .set('take', take.toString());
    return this.http
      .get<ApiSingleResponse<NotificationListResponse>>(this.base, { params })
      .pipe(
        tap((res) => {
          this._notifications.next(res.data?.items ?? []);
          this._unreadCount.next(res.data?.unreadCount ?? 0);
        }),
      );
  }

  markAsRead(id: number): Observable<ApiSimpleResponse> {
    return this.http
      .patch<ApiSimpleResponse>(`${this.base}/${id}/read`, {})
      .pipe(
        tap(() => {
          const current = this._notifications.getValue();
          const wasUnread = current.some((n) => n.id === id && !n.isRead);

          this._notifications.next(
            current.map((n) => (n.id === id ? { ...n, isRead: true } : n)),
          );

          // On décrémente plutôt que de recompter la page courante : au-delà
          // de `take` notifications, un recomptage local ferait chuter le
          // badge au nombre de non-lues visibles, pas au total réel.
          if (wasUnread) {
            this._unreadCount.next(Math.max(0, this._unreadCount.getValue() - 1));
          }
        }),
      );
  }

  markAllAsRead(): Observable<ApiSimpleResponse> {
    return this.http
      .patch<ApiSimpleResponse>(`${this.base}/read-all`, {})
      .pipe(
        tap(() => {
          const updated = this._notifications
            .getValue()
            .map((n) => ({ ...n, isRead: true }));
          this._notifications.next(updated);
          this._unreadCount.next(0);
        }),
      );
  }

  /** Increment the unread badge by 1 (called by NotificationHubService on push). */
  incrementUnread(): void {
    this._unreadCount.next(this._unreadCount.getValue() + 1);
  }

  /** Push a new notification to the top of the local list. */
  prependNotification(notification: NotificationDto): void {
    const updated = [notification, ...this._notifications.getValue()];
    this._notifications.next(updated);
    this._unreadCount.next(this._unreadCount.getValue() + 1);
  }
}
